import { createAction } from 'typesafe-actions';
import {
  BlockManagerAddContactRequest,
  BlockManagerGetFlatRequest,
  BlockManagerGetFlatsRequest,
  BlockManagerGetPropertiesRequest,
  BlockManagerUpdateContactRequest,
  BlockManagerListContactsRequest,
  EngageFlatIsHMO,
  EngageFlatIsCommerciallyOwned,
  BlockManagerGetScheduledVisitsRequest,
  BlockManagerPostScheduledVisitRequest,
  EngageScheduledVisitStatus,
  BlockManagerCancelScheduledVisitRequest,
  BlockManagerGetScheduledVisitRecipientsRequest,
  ScheduledVisitMessageTemplate,
  ScheduledVisitCommunicationSchedule,
  BlockManagerGetScheduledVisitCommunicationScheduleRequest,
  EngageContactEvacuationInfo,
  EngageContactInteractionLog,
} from 'types/DTOs/BlockManagerDTOs';
import {
  EngageFlatContact,
  EngageFlatListItem,
  EngageFlatsFilter,
  EngageInspector,
  EngagePropertiesFilter,
  EngagePropertyListItem,
} from 'types/Engage';
import { FileBeingAdded } from 'service/file/types';

export const eventTypeEngageContactAdded = createAction(
  'eventTypeEngageContactAdded',
)<{
  contactId: string;
}>();

export const doEngageClearData = createAction('doEngageClearData')<{
  key: string;
}>();

export const doEngageSetLoadState = createAction('doEngageSetLoadState')<{
  key: string;
  loading: boolean;
}>();

/**
 * Properties
 */

export const doEngagePropertiesLoad = createAction('doEngagePropertiesLoad')<{
  key: string;
  page: 'reset' | 'next';
}>();

export const doEngagePropertiesSetData = createAction(
  'doEngagePropertiesSetData',
)<{
  key: string;
  response: BlockManagerGetPropertiesRequest['response'];
  clearData: boolean;
  pageNumber: number;
}>();

export const doEngagePropertiesSetFilter = createAction(
  'doEngagePropertiesSetFilter',
)<{
  key: string;
  data: Partial<EngagePropertiesFilter>;
  replace?: boolean;
}>();

/**
 * Flats
 */

export const doEngageFlatsLoad = createAction('doEngageFlatsLoad')<{
  propertyId: string;
  key: string;
  page: 'reset' | 'next';
}>();

export const doEngageFlatsSetFilter = createAction('doEngageFlatsSetFilter')<{
  key: string;
  data: Partial<EngageFlatsFilter>;
  replace?: boolean;
}>();

export const doEngageOnboard = createAction('doEngageOnboard')<{
  propertyId: string;
  key: string;
}>();

export const doEngageFlatsSetData = createAction('doEngageFlatsSetData')<{
  key: string;
  response: BlockManagerGetFlatsRequest['response'];
  clearData: boolean;
  pageNumber: number;
}>();

export const doEngageFlatsClearData = createAction('doEngageFlatsClearData')<{
  key: string;
}>();

export const doEngageUploadContacts = createAction('doEngageUploadContacts')<{
  key: string;
  propertyId: string;
  fileName: string;
  file: FileBeingAdded;
}>();

export const doEngageDownloadFlatReferences = createAction(
  'doEngageDownloadFlatReferences',
)<{
  key: string;
  propertyId: string;
}>();

export const doEngageScheduledVisitSave = createAction(
  'doEngageScheduledVisitSave',
)<{
  propertyId: string;
  pageKey: string;
  timeslots: BlockManagerPostScheduledVisitRequest['body']['timeslots'];
  scheduledVisitId?: string;
  status: EngageScheduledVisitStatus;
  inspectorIds: string[];
  currentStatus: EngageScheduledVisitStatus;
}>();

export const doEngageScheduledVisitDelete = createAction(
  'doEngageScheduledVisitDelete',
)<{
  propertyId: string;
  pageKey: string;
  scheduledVisitId: string;
}>();

export const doEngageScheduledVisitCancel = createAction(
  'doEngageScheduledVisitCancel',
)<{
  propertyId: string;
  pageKey: string;
  scheduledVisitId: string;
  cancelledReason: BlockManagerCancelScheduledVisitRequest['body']['cancelledReason'];
}>();

export const doEngageScheduledVisitTemplatesLoad = createAction(
  'doEngageScheduledVisitTemplatesLoad',
)<{
  key: string;
}>();

export const doEngageScheduledVisitTemplatesSet = createAction(
  'doEngageScheduledVisitTemplatesSet',
)<{
  key: string;
  templates: ScheduledVisitMessageTemplate[];
}>();

// This is purely for loading from the saga.
export const doEngageScheduledVisitCommunicationScheduleLoad = createAction(
  'doEngageScheduledVisitCommunicationScheduleLoad',
)<{
  key: string;
  propertyUid: BlockManagerGetScheduledVisitCommunicationScheduleRequest['params']['propertyUid'];
  scheduledVisitId: BlockManagerGetScheduledVisitCommunicationScheduleRequest['params']['scheduledVisitId'];
}>();

export const doEngageScheduledVisitCommunicationScheduleUnload = createAction(
  'doEngageScheduledVisitCommunicationScheduleUnload',
)<{
  key: string;
}>();

export const doEngageScheduledVisitCommunicationScheduleSet = createAction(
  'doEngageScheduledVisitCommunicationScheduleSet',
)<{
  key: string;
  communicationSchedule: ScheduledVisitCommunicationSchedule[];
  scheduledVisitId: BlockManagerGetScheduledVisitCommunicationScheduleRequest['params']['scheduledVisitId'];
}>();

export const doEngageDownloadTemplate = createAction(
  'doEngageDownloadTemplate',
)<{
  key: string;
}>();

/**
 * Flat
 */

export const doEngageFlatLoad = createAction('doEngageFlatLoad')<{
  key: string;
  property: EngagePropertyListItem;
  flat: EngageFlatListItem;
}>();

export const doEngageFlatSetData = createAction('doEngageFlatSetData')<{
  key: string;
  property: EngagePropertyListItem;
  flat: EngageFlatListItem;
  response: BlockManagerGetFlatRequest['response'];
}>();

export const doEngageFlatUpdateData = createAction('doEngageFlatUpdateData')<{
  key: string;
  occupationStatus?: EngageFlatListItem['occupationStatus'];
  isHMO?: EngageFlatIsHMO;
  isCommerciallyOwned?: EngageFlatIsCommerciallyOwned;
  issues?: EngageFlatListItem['issues'];
  archivedContactIds?: string[];
  unarchivedContactIds?: string[];
  contacts?: BlockManagerGetFlatRequest['response']['contacts'];
  communications?: BlockManagerGetFlatRequest['response']['communications'];
}>();

export const doEngageFlatChangeOccupationStatus = createAction(
  'doEngageFlatChangeOccupationStatus',
)<{
  key: string;
}>();

export const doEngageFlatChangeIsHMO = createAction('doEngageFlatChangeIsHMO')<{
  key: string;
}>();

export const doEngageFlatChangeIsCommerciallyOwned = createAction(
  'doEngageFlatChangeIsCommerciallyOwned',
)<{
  key: string;
}>();

/**
 * Contacts
 */
export const doEngageFlatArchivedContactsLoad = createAction(
  'doEngageFlatArchivedContactsLoad',
)<{
  key: string;
  propertyId: string;
  flatId: string;
}>();

export const doEngageFlatArchivedContactsSet = createAction(
  'doEngageFlatArchivedContactsSet',
)<{
  key: string;
  contacts: BlockManagerListContactsRequest['response']['contacts'];
}>();

export const doEngageArchiveContact = createAction('doEngageArchiveContact')<{
  key: string;
  propertyId: string;
  flatId: string;
  contactId: string;
}>();

export const doEngageUnarchiveContact = createAction(
  'doEngageUnarchiveContact',
)<{
  key: string;
  propertyId: string;
  flatId: string;
  contactId: string;
}>();

export const doEngageSaveContact = createAction('doEngageSaveContact')<{
  key: string;
  propertyId: string;
  flatId: string;
  isNew: boolean;
  contact: Omit<EngageFlatContact, 'hasSupportNeeds' | 'interactionLogs'>;
  actionAfterSave?: 'openEmergencyEvacuationDetails';
}>();

export const doEngageContactEvacuationInfoLoad = createAction(
  'doEngageContactEvacuationInfoLoad',
)<{
  key: string;
  propertyId: string;
  flatId: string;
  contactId: string;
  contactName: string;
}>();

export const doEngageContactEvacuationInfoSave = createAction(
  'doEngageContactEvacuationInfoSave',
)<{
  key: string;
  propertyId: string;
  flatId: string;
  contactId: string;
  info: EngageContactEvacuationInfo;
}>();

export const doEngageContactInteractionLogAdd = createAction(
  'doEngageContactInteractionLogAdd',
)<{
  key: string;
  flatId: string;
  contactId: string;
  log: EngageContactInteractionLog;
}>();

export const doEngageContactUpdate = createAction('doEngageContactUpdate')<{
  key: string;
  isNew: boolean;
  propertyId: string;
  flatId: string;
  contact:
    | BlockManagerAddContactRequest['response']['contact']
    | BlockManagerUpdateContactRequest['response']['contact'];
}>();

/**
 * Communication
 */

export const doEngageSendCommunication = createAction(
  'doEngageSendCommunication',
)<{
  key: string;
  propertyId: string;
  communicationId: string;
  flatId: string;
  contactIds: string[];
}>();

export const doEngagePreviewCommunication = createAction(
  'doEngagePreviewCommunication',
)<{
  key: string;
  propertyId: string;
  flatId: string;
  communicationId: string;
  communicationName: string;
}>();

export const doEngageSendCommunicationBulk = createAction(
  'doEngageSendCommunicationBulk',
)<{
  key: string;
  propertyId: string;
  communicationId: string;
  flatIds: string[];
  residents: boolean;
  owners: boolean;
  primaryContactsOnly?: boolean;
}>();

/**
 * Scheduled Visits
 */

export const doEngageScheduledVisitsLoad = createAction(
  'doEngageScheduledVisitsLoad',
)<{
  key: string;
  propertyId: string;
}>();

export const doEngageScheduledVisitsSetData = createAction(
  'doEngageScheduledVisitsSetData',
)<{
  key: string;
  response: BlockManagerGetScheduledVisitsRequest['response'];
}>();

export const doEngageScheduledVisitRecipientsLoad = createAction(
  'doEngageScheduledVisitRecipientsLoad',
)<{
  key: string;
  propertyId: string;
}>();

export const doEngageScheduledVisitRecipientsUnload = createAction(
  'doEngageScheduledVisitRecipientsUnload',
)<{
  key: string;
}>();

export const doEngageScheduledVisitRecipientsSetData = createAction(
  'doEngageScheduledVisitRecipientsSetData',
)<{
  key: string;
  data: BlockManagerGetScheduledVisitRecipientsRequest['response'];
}>();

/**
 * Inspector
 */

export const doEngageOpenInspectorSelector = createAction(
  'doEngageOpenInspectorSelector',
)<{
  key: string;
  propertyId: string;
  inspectorIds: string[];
}>();

export const eventTypeInspectorsSelected = createAction(
  'eventTypeInspectorsSelected',
)<{
  eventKey: string;
  accounts: EngageInspector[];
}>();

export const eventTypeScheduledVisitAdded = createAction(
  'eventTypeScheduledVisitAdded',
)<{
  scheduledVisitId: string;
}>();
